import { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { productDetails } from '../../mocks/productDetails';
import ImageGallery from './components/ImageGallery';
import ProductInfo from './components/ProductInfo';
import ProductDetails from './components/ProductDetails';
import RelatedProducts from './components/RelatedProducts';
import TrustBadges from './components/TrustBadges';
import StickyAddToCart from './components/StickyAddToCart';

export default function ProductPage() {
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();
  const [showScrollTop, setShowScrollTop] = useState(false);

  const product = productDetails.find(p => p.id === Number(id));

  useEffect(() => {
    window.scrollTo(0, 0);
  }, [id]);

  useEffect(() => {
    const handleScroll = () => {
      setShowScrollTop(window.scrollY > 400);
    };

    window.addEventListener('scroll', handleScroll);
    return () => window.removeEventListener('scroll', handleScroll);
  }, []);

  if (!product) {
    return (
      <div className="min-h-screen bg-[#0A0A0A] flex items-center justify-center">
        <div className="text-center">
          <h1 className="text-4xl font-bold text-white mb-4">Product Not Found</h1>
          <p className="text-white/60 mb-8">The product you're looking for doesn't exist.</p>
          <button
            onClick={() => navigate('/shop')}
            className="bg-white text-black px-8 py-3 rounded-lg font-bold hover:bg-white/90 transition-colors whitespace-nowrap"
          >
            Back to Shop
          </button>
        </div>
      </div>
    );
  }

  const handleAddToCart = () => {
    console.log('Add to cart clicked');
  };

  return (
    <div className="min-h-screen bg-[#0A0A0A]">
      {/* Navigation */}
      <nav className="sticky top-0 bg-black/95 backdrop-blur-lg border-b border-white/10 z-40">
        <div className="max-w-7xl mx-auto px-6 py-4">
          <div className="flex items-center justify-between">
            <button
              onClick={() => navigate(-1)}
              className="flex items-center gap-2 text-white/60 hover:text-white transition-colors"
            >
              <i className="ri-arrow-left-line text-xl"></i>
              <span className="font-medium">Back</span>
            </button>

            <a href="/" className="flex items-center">
              <img
                src="https://static.readdy.ai/image/577c45d6defa6ac02f4bf39c73856fa0/5b9bd2cbb4f3f977486d4a8e60efd124.jpeg"
                alt="BillPlay Apparel"
                className="h-[50px] w-auto object-contain"
              />
            </a>

            <div className="flex items-center gap-6">
              <a href="/shop" className="text-white/60 hover:text-white transition-colors font-medium">
                Shop
              </a>
              <button className="relative text-white hover:text-white/80 transition-colors">
                <i className="ri-shopping-bag-line text-2xl"></i>
                <span className="absolute -top-1 -right-1 w-5 h-5 bg-white text-black text-xs font-bold rounded-full flex items-center justify-center">
                  0
                </span>
              </button>
            </div>
          </div>
        </div>
      </nav>

      {/* Breadcrumb */}
      <div className="max-w-7xl mx-auto px-6 py-6">
        <div className="flex items-center gap-2 text-sm text-white/60">
          <a href="/" className="hover:text-white transition-colors">Home</a>
          <i className="ri-arrow-right-s-line"></i>
          <a href="/shop" className="hover:text-white transition-colors">Shop</a>
          <i className="ri-arrow-right-s-line"></i>
          <a href={`/${product.category}`} className="hover:text-white transition-colors capitalize">
            {product.category}
          </a>
          <i className="ri-arrow-right-s-line"></i>
          <span className="text-white">{product.name}</span>
        </div>
      </div>

      {/* Product Section */}
      <div className="max-w-7xl mx-auto px-6 pb-20">
        <div className="grid lg:grid-cols-2 gap-12 mb-20">
          {/* Image Gallery */}
          <div>
            <ImageGallery images={product.images} productName={product.name} />
          </div>

          {/* Product Info */}
          <div className="lg:sticky lg:top-24 lg:self-start">
            <ProductInfo product={product} />
          </div>
        </div>

        {/* Trust Badges */}
        <div className="mb-20">
          <TrustBadges />
        </div>

        {/* Product Details Tabs */}
        <div className="mb-20">
          <ProductDetails product={product} />
        </div>

        {/* Related Products */}
        <RelatedProducts currentProductId={product.id} category={product.category} />
      </div>

      {/* Sticky Add to Cart (Mobile) */}
      <StickyAddToCart
        productName={product.name}
        price={product.price}
        onAddToCart={handleAddToCart}
      />

      {/* Scroll to Top Button */}
      {showScrollTop && (
        <button
          onClick={() => window.scrollTo({ top: 0, behavior: 'smooth' })}
          className="fixed bottom-24 right-6 w-12 h-12 bg-white text-black rounded-full flex items-center justify-center hover:bg-white/90 transition-all duration-300 z-40 shadow-lg"
        >
          <i className="ri-arrow-up-line text-xl"></i>
        </button>
      )}
    </div>
  );
}